import React, { useEffect, useState, useRef, useCallback, useLayoutEffect } from "react";
import {
    FaEye,
    FaMapMarkerAlt,
    FaFilter,
    FaSearch,
    FaTimes,
    FaChevronDown,
    FaChevronLeft,
    FaChevronRight,
} from "react-icons/fa";

import Navbar from "./Navbar";
import Footer from "./Footer";
import { submitEnquiry } from "../utils/submitEnquiry";
import {
    canSubmitEnquiryForm,
    sanitizeIndianMobileInput,
} from "../utils/phoneValidation";
import {
    handleExclusiveVideoPause,
    handleExclusiveVideoPlay,
} from "../utils/exclusiveVideoPlayback";
import { allProperties } from "../data/propertiesData";

/** Shared prev/next control styling (main image, thumbnail strip, property row scroll) */
const GALLERY_NAV_BTN_STYLES =
    "flex h-11 w-11 items-center justify-center rounded-full border-2 border-[#3B2B9A] dark:border-indigo-400 bg-white dark:bg-slate-800 text-[#3B2B9A] dark:text-indigo-300 shadow-[0_4px_14px_rgba(59,43,154,0.35)] ring-2 ring-white dark:ring-slate-900 transition-all duration-200 hover:bg-[#3B2B9A] hover:text-white dark:hover:bg-[#3B2B9A] dark:hover:text-white hover:shadow-[0_6px_20px_rgba(59,43,154,0.45)] hover:scale-105 active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#3B2B9A]/35 disabled:pointer-events-none disabled:border-slate-300 disabled:bg-slate-100 dark:disabled:bg-slate-800 disabled:text-slate-400 disabled:shadow-none disabled:opacity-90 disabled:hover:scale-100 disabled:hover:bg-slate-100 dark:disabled:hover:bg-slate-800 disabled:hover:text-slate-400 sm:h-12 sm:w-12";

function PropertyGalleryThumbnails({
    propertyId,
    propertyMedia,
    activeIndex,
    setActiveMediaByProperty,
}) {
    const stripRef = useRef(null);

    const setIndex = (idx) => {
        setActiveMediaByProperty((prev) => ({
            ...prev,
            [propertyId]: Math.max(0, Math.min(idx, propertyMedia.length - 1)),
        }));
    };

    useLayoutEffect(() => {
        const strip = stripRef.current;
        if (!strip) return;
        const activeBtn = strip.querySelector(
            `button[data-property="${propertyId}"][data-thumb-index="${activeIndex}"]`
        );
        activeBtn?.scrollIntoView({
            behavior: "smooth",
            inline: "nearest",
            block: "nearest",
        });
    }, [activeIndex, propertyId, propertyMedia.length]);

    const total = propertyMedia.length;
    const hasPrev = activeIndex > 0;
    const hasNext = activeIndex < total - 1;

    const arrowClass = `relative z-10 shrink-0 ${GALLERY_NAV_BTN_STYLES}`;

    return (
        <div className="mt-3">
            <div className="flex items-center gap-3 sm:gap-4">
                <button
                    type="button"
                    className={arrowClass}
                    aria-label="Previous gallery image"
                    disabled={!hasPrev}
                    title={hasPrev ? "Previous image" : "You are on the first image"}
                    onClick={() => setIndex(activeIndex - 1)}
                >
                    <FaChevronLeft className="text-lg sm:text-xl drop-shadow-sm" aria-hidden />
                </button>

                <div
                    ref={stripRef}
                    className="min-w-0 flex-1 overflow-x-auto scrollbar-hide pb-1"
                >
                    <div className="flex gap-2 min-w-max">
                        {propertyMedia.map((media, mediaIndex) => (
                            <button
                                key={`${propertyId}-${media.type}-${mediaIndex}`}
                                type="button"
                                data-property={propertyId}
                                data-thumb-index={mediaIndex}
                                onClick={() => setIndex(mediaIndex)}
                                className={`w-16 h-12 shrink-0 rounded-lg border-2 overflow-hidden transition-all ${
                                    activeIndex === mediaIndex
                                        ? "border-[#3B2B9A] shadow-sm ring-2 ring-[#3B2B9A]/20"
                                        : "border-slate-200 dark:border-slate-600 hover:border-[#5A47D8]"
                                }`}
                            >
                                {media.type === "video" ? (
                                    <div className="w-full h-full bg-slate-900 text-white text-[10px] font-semibold flex items-center justify-center">
                                        Video
                                    </div>
                                ) : (
                                    <img
                                        src={media.src}
                                        alt={media.label}
                                        className="w-full h-full object-cover"
                                    />
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                <button
                    type="button"
                    className={arrowClass}
                    aria-label="Next gallery image"
                    disabled={!hasNext}
                    title={hasNext ? "Next image" : "You are on the last image"}
                    onClick={() => setIndex(activeIndex + 1)}
                >
                    <FaChevronRight className="text-lg sm:text-xl drop-shadow-sm" aria-hidden />
                </button>
            </div>

            <div
                className="relative z-0 mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-200 ring-1 ring-slate-300/80"
                role="progressbar"
                aria-valuenow={activeIndex + 1}
                aria-valuemin={1}
                aria-valuemax={total}
                aria-label={`Gallery image ${activeIndex + 1} of ${total}`}
            >
                <div
                    className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#93c5fd] to-[#6366f1] shadow-sm transition-[transform,width] duration-300 ease-out"
                    style={{
                        width: `${100 / total}%`,
                        transform: `translateX(${activeIndex * 100}%)`,
                    }}
                />
            </div>
        </div>
    );
}

export default function Properties() {
    const [contactOpen, setContactOpen] = useState(false);
    const [selectedType, setSelectedType] = useState("All");
    const [searchTerm, setSearchTerm] = useState("");
    const [sortBy, setSortBy] = useState("featured");
    const [showSortDropdown, setShowSortDropdown] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeMediaByProperty, setActiveMediaByProperty] = useState({ 1: 0 });
    const [enquirySubmitting, setEnquirySubmitting] = useState(false);
    const propertiesPerPage = 12; // up to 3 rows × 4 columns per page
    const propertiesRowRef = useRef(null);
    const [propsRowScroll, setPropsRowScroll] = useState({ canLeft: false, canRight: false });

    const updatePropsRowScrollState = useCallback(() => {
        const el = propertiesRowRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        const maxScroll = scrollWidth - clientWidth;
        setPropsRowScroll({
            canLeft: scrollLeft > 4,
            canRight: maxScroll > 4 && scrollLeft < maxScroll - 4,
        });
    }, []);

    const scrollPropertiesRow = (direction) => {
        const el = propertiesRowRef.current;
        if (!el) return;
        const firstCard = el.querySelector("article");
        const gap =
            typeof window !== "undefined"
                ? parseInt(getComputedStyle(el).columnGap || getComputedStyle(el).gap, 10) || 24
                : 24;
        const step = firstCard ? firstCard.offsetWidth + gap : el.clientWidth * 0.85;
        el.scrollBy({ left: direction * step, behavior: "smooth" });
    };
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });
    const [interestOpen, setInterestOpen] = useState(false);
    const [interest, setInterest] = useState("I'm interested in");

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedType, searchTerm, sortBy]);

    // Scroll to top when page changes
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentPage]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const filteredProperties = allProperties.filter((property) => {
        const matchesType = selectedType === "All" || property.type === selectedType;
        const matchesSearch =
            property.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            property.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
            property.subLocation.toLowerCase().includes(searchTerm.toLowerCase());

        return matchesType && matchesSearch;
    }).sort((a, b) => {
        if (sortBy === "featured") {
            return b.featured - a.featured;
        } else if (sortBy === "price-low") {
            return a.price - b.price;
        } else if (sortBy === "price-high") {
            return b.price - a.price;
        }
        return 0;
    });

    // Pagination logic
    const indexOfLastProperty = currentPage * propertiesPerPage;
    const indexOfFirstProperty = indexOfLastProperty - propertiesPerPage;
    const currentProperties = filteredProperties.slice(indexOfFirstProperty, indexOfLastProperty);
    const totalPages = Math.ceil(filteredProperties.length / propertiesPerPage);

    useEffect(() => {
        const el = propertiesRowRef.current;
        if (el) el.scrollLeft = 0;
        const id = requestAnimationFrame(() => updatePropsRowScrollState());
        return () => cancelAnimationFrame(id);
    }, [currentPage, currentProperties.length, updatePropsRowScrollState]);

    useEffect(() => {
        const el = propertiesRowRef.current;
        if (!el) return;
        const onScroll = () => updatePropsRowScrollState();
        el.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => {
            el.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
        };
    }, [currentPage, filteredProperties.length, updatePropsRowScrollState]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === "phone") {
            setFormData((prev) => ({
                ...prev,
                phone: sanitizeIndianMobileInput(value),
            }));
            return;
        }
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const callbackFormValid = canSubmitEnquiryForm(formData);

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmitEnquiryForm(formData)) return;

        const phone = sanitizeIndianMobileInput(formData.phone);
        const payload = {
            name: formData.name.trim(),
            email: formData.email.trim(),
            phone,
            property:
                interest && interest !== "I'm interested in"
                    ? interest
                    : "General enquiry",
            message: (formData.message || "").trim(),
            source: "Properties page — Contact Us",
            submittedAt: new Date().toISOString(),
        };

        setEnquirySubmitting(true);
        try {
            await submitEnquiry(payload);
            setContactOpen(false);
            setFormData({
                name: "",
                email: "",
                phone: "",
                message: "",
            });
            setInterest("I'm interested in");
        } catch (error) {
            console.error("Enquiry submission failed:", error);
            alert(
                error.message ||
                    "Sorry, we could not submit your enquiry right now. Please try again."
            );
        } finally {
            setEnquirySubmitting(false);
        }
    };

    return (
        <div className="min-h-screen font-sans text-slate-800 dark:text-slate-100 overflow-x-hidden bg-slate-50 dark:bg-slate-950">
            <Navbar />

            {/* ENHANCED HERO SECTION */}
            <section className="pt-20 sm:pt-24 lg:pt-0 relative  min-h-screen flex items-center bg-cover bg-center flex items-center overflow-hidden">
                {/* Background Image with Parallax Effect */}
                <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-1000"
                    style={{
                        backgroundImage: "url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=2000')",
                        transform: "scale(1.1)"
                    }}
                />

                {/* Animated Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#3B2B9A]/85 via-[#4A3AB5]/70 to-[#D83B29]/50 animate-gradient"></div>

                {/* Floating Geometric Shapes */}
                <div className="absolute inset-0 opacity-20 overflow-hidden pointer-events-none">
                    <div className="absolute top-20 left-10 w-32 h-32 border-4 border-white/30 rounded-full animate-float-slow"></div>
                    <div className="absolute top-40 right-20 w-24 h-24 border-4 border-white/20 rotate-45 animate-float-slower"></div>
                    <div className="absolute bottom-32 left-1/4 w-40 h-40 border-4 border-white/25 rounded-full animate-float"></div>
                    <div className="absolute bottom-20 right-1/3 w-20 h-20 bg-white/10 backdrop-blur-sm rotate-12 animate-float-slow"></div>
                </div>

                {/* Main Content */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center w-full">
                    {/* Badge */}
                    <div className="inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-2.5 
            bg-white/10 backdrop-blur-md border border-white/20 rounded-full 
            text-white text-xs sm:text-sm font-medium mb-4 sm:mb-6
            shadow-lg animate-fade-in-down">
                        <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                        Premium Properties Available Now
                    </div>

                    {/* Main Heading with Stagger Animation */}
                    <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white leading-tight drop-shadow-2xl mb-4 sm:mb-6 animate-fade-in">
                        Explore Premium{" "}
                        <span className="relative inline-block">
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-orange-300 to-orange-400 animate-gradient-text">
                                Properties
                            </span>
                            <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-yellow-300 to-orange-400 rounded-full animate-expand"></span>
                        </span>
                    </h1>

                    {/* Subtitle */}
                    <p className="mt-2 sm:mt-4 text-sm sm:text-base md:text-lg lg:text-xl text-white/95 max-w-3xl mx-auto px-4 leading-relaxed animate-fade-in-up">
                        Discover exceptional plots and farmhouses in prime locations across NCR
                        <span className="block mt-2 text-xs sm:text-sm md:text-base text-white/80">
                            Investment opportunities starting from ₹9,500 per sq yd
                        </span>
                    </p>

                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mt-6 sm:mt-8 md:mt-10 animate-fade-in-up-delayed">
                        <button
                            onClick={() => {
                                document.getElementById('properties-grid')?.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'start'
                                });
                            }}
                            className="group relative overflow-hidden px-6 sm:px-8 py-3 sm:py-4 
            bg-white dark:bg-slate-100 text-[#3B2B9A] dark:text-violet-900 font-bold rounded-full
            shadow-2xl hover:shadow-[0_20px_60px_rgba(255,255,255,0.3)]
            transition-all duration-300 hover:scale-105
            text-sm sm:text-base w-full sm:w-auto"
                        >
                            <span className="relative z-10 flex items-center justify-center gap-2">
                                Browse Properties
                                <svg
                                    className="w-4 h-4 group-hover:translate-x-1 transition-transform"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                                    />
                                </svg>
                            </span>
                            <div className="absolute inset-0 bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                        </button>

                        <button
                            onClick={() => setContactOpen(true)}
                            className="group px-6 sm:px-8 py-3 sm:py-4 
                                bg-white/10 backdrop-blur-md text-white font-bold rounded-full
                                border-2 border-white/30 hover:border-white/60 hover:bg-white/20
                                transition-all duration-300 hover:scale-105
                                text-sm sm:text-base w-full sm:w-auto">
                            <span className="flex items-center justify-center gap-2">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                Contact Expert
                            </span>
                        </button>
                    </div>

                    {/* Stats Bar */}
                    <div className="grid grid-cols-3 gap-4 sm:gap-6 md:gap-8 max-w-3xl mx-auto mt-8 sm:mt-12 md:mt-16 
            bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl 
            p-4 sm:p-6 shadow-2xl animate-fade-in-up-more-delayed">
                        <div className="text-center">
                            <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-1">
                                50+
                            </div>
                            <div className="text-xs sm:text-sm text-white/80">
                                Properties
                            </div>
                        </div>
                        <div className="text-center border-x border-white/20">
                            <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-1">
                                1200+
                            </div>
                            <div className="text-xs sm:text-sm text-white/80">
                                Happy Clients
                            </div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-1">
                                15+
                            </div>
                            <div className="text-xs sm:text-sm text-white/80">
                                Locations
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scroll Indicator */}
                <button
                    type="button"
                    aria-label="Scroll to properties"
                    onClick={() =>
                        document.getElementById('properties-grid')?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                        })
                    }
                    className="absolute bottom-8 left-1/2 z-30 -translate-x-1/2 cursor-pointer group rounded-full border-0 bg-transparent px-5 py-3 text-left hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                >
                    <span className="hero-scroll-affordance flex flex-col items-center gap-2 sm:gap-3">
                        <span className="text-white/60 text-[10px] sm:text-xs font-medium uppercase tracking-wider group-hover:text-white transition-colors">
                            Scroll to explore
                        </span>
                        <div className="w-6 h-9 sm:w-7 sm:h-11 border-2 border-white/40 rounded-full flex items-start justify-center p-1.5 sm:p-2 group-hover:border-white/80 transition-all">
                            <div className="hero-scroll-wheel w-1 h-2 sm:w-1.5 sm:h-3 rounded-full bg-white/70"></div>
                        </div>
                    </span>
                </button>
            </section>

            <style>{`
    @keyframes gradient {
        0%, 100% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
    }

    @keyframes float {
        0%, 100% { transform: translateY(0px) rotate(0deg); }
        50% { transform: translateY(-20px) rotate(5deg); }
    }

    @keyframes float-slow {
        0%, 100% { transform: translateY(0px) rotate(0deg); }
        50% { transform: translateY(-30px) rotate(-5deg); }
    }

    @keyframes float-slower {
        0%, 100% { transform: translateY(0px) rotate(45deg); }
        50% { transform: translateY(-15px) rotate(50deg); }
    }

    @keyframes fade-in-down {
        0% { opacity: 0; transform: translateY(-20px); }
        100% { opacity: 1; transform: translateY(0); }
    }

    @keyframes fade-in {
        0% { opacity: 0; transform: scale(0.95); }
        100% { opacity: 1; transform: scale(1); }
    }

    @keyframes fade-in-up {
        0% { opacity: 0; transform: translateY(20px); }
        100% { opacity: 1; transform: translateY(0); }
    }

    @keyframes expand {
        0% { width: 0; }
        100% { width: 100%; }
    }

    @keyframes gradient-text {
        0%, 100% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
    }

    .animate-gradient {
        background-size: 200% 200%;
        animation: gradient 8s ease infinite;
    }

    .animate-float { animation: float 6s ease-in-out infinite; }
    .animate-float-slow { animation: float-slow 8s ease-in-out infinite; }
    .animate-float-slower { animation: float-slower 10s ease-in-out infinite; }
    .animate-fade-in-down { animation: fade-in-down 0.8s ease-out; }
    .animate-fade-in { animation: fade-in 1s ease-out 0.2s both; }
    .animate-fade-in-up { animation: fade-in-up 0.8s ease-out 0.4s both; }
    .animate-fade-in-up-delayed { animation: fade-in-up 0.8s ease-out 0.6s both; }
    .animate-fade-in-up-more-delayed { animation: fade-in-up 0.8s ease-out 0.8s both; }
    .animate-expand { animation: expand 0.8s ease-out 1.2s both; }
    
    .animate-gradient-text {
        background-size: 200% auto;
        animation: gradient-text 3s ease infinite;
    }
`}</style>

            {/* ENHANCED FILTERS & SEARCH - All in One Row */}
            <section className="sticky top-[72px] z-30 border-b border-slate-200 dark:border-slate-600 backdrop-blur-xl bg-white/95 dark:bg-slate-900/95 shadow-lg">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-6">
                    <div className="space-y-4">
                        {/* Single Row Layout - Search, Filters, and Sort */}
                        <div className="flex gap-3 items-center overflow-visible">
                            {/* Search Bar - Flexible width */}
                            <div className="relative flex-1 min-w-0 group">
                                <div className="absolute inset-0 bg-gradient-to-r from-[#3B2B9A]/20 to-[#D83B29]/20 rounded-2xl blur-xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-500"></div>
                                <div className="relative">
                                    <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#3B2B9A] dark:group-focus-within:text-indigo-300 text-sm sm:text-base transition-colors duration-300" />
                                    <input
                                        type="text"
                                        placeholder="Search properties..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-11 sm:pl-12 pr-10 py-3 rounded-2xl 
                                border-2 border-slate-200 dark:border-slate-600 bg-slate-50/50 dark:bg-slate-900/80
                                focus:border-[#3B2B9A] focus:bg-white dark:focus:bg-slate-900 focus:outline-none 
                                focus:ring-4 focus:ring-[#3B2B9A]/10 focus:shadow-xl
                                transition-all duration-300 text-sm sm:text-base text-slate-900 dark:text-slate-100
                                placeholder:text-slate-400 dark:placeholder:text-slate-500"
                                    />
                                    {searchTerm && (
                                        <button
                                            onClick={() => setSearchTerm("")}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 
                                    p-1.5 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 
                                    text-slate-500 hover:text-slate-700 dark:text-slate-200
                                    transition-all duration-300 hover:scale-110 hover:rotate-90"
                                        >
                                            <FaTimes className="text-xs" />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Divider */}
                            <div className="hidden lg:block w-px h-10 bg-slate-200"></div>

                            {/* Filter Buttons */}
                            <div className="hidden lg:flex gap-2 items-center flex-shrink-0">
                                <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-900 rounded-xl border border-slate-200 dark:border-slate-600 whitespace-nowrap">
                                    <FaFilter className="text-xs text-[#3B2B9A] dark:text-indigo-300" />
                                    <span className="text-slate-800 dark:text-slate-100 font-semibold text-xs">
                                        Type:
                                    </span>
                                </div>
                                {["All", "Plot", "Farmhouse"].map((type) => (
                                    <button
                                        key={type}
                                        onClick={() => setSelectedType(type)}
                                        className={`relative px-4 py-2.5 rounded-xl font-semibold 
                                transition-all duration-300 whitespace-nowrap text-sm
                                ${selectedType === type
                                                ? "bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] text-white shadow-lg shadow-[#3B2B9A]/30"
                                                : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:bg-slate-950 border-2 border-slate-200 dark:border-slate-600 hover:border-slate-300"
                                            }`}
                                    >
                                        {selectedType === type && (
                                            <span className="absolute inset-0 rounded-xl bg-white/20 animate-pulse"></span>
                                        )}
                                        <span className="relative flex items-center gap-1.5">
                                            {type === "All" && "🏘️"}
                                            {type === "Plot" && "🏗️"}
                                            {type === "Farmhouse" && "🌾"}
                                            {type}
                                        </span>
                                    </button>
                                ))}
                            </div>

                            {/* Divider */}
                            <div className="hidden lg:block w-px h-10 bg-slate-200"></div>

                            {/* Sort Dropdown */}
                            <div className="relative flex-shrink-0">
                                <button
                                    onClick={() => setShowSortDropdown(!showSortDropdown)}
                                    className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-slate-200 dark:border-slate-600
                            bg-gradient-to-r from-white to-slate-50 dark:from-slate-800 dark:to-slate-900
                            hover:border-[#3B2B9A] hover:shadow-lg
                            transition-all duration-300 text-sm font-medium text-slate-800 dark:text-slate-100 whitespace-nowrap"
                                >
                                    <span className="flex items-center gap-2">
                                        <svg className="w-4 h-4 text-[#3B2B9A] dark:text-indigo-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                                        </svg>
                                        <span className="hidden xl:inline text-slate-600 dark:text-slate-400 font-normal">Sort:</span>
                                        <span className="font-semibold text-slate-900 dark:text-white tabular-nums">
                                            {sortBy === "featured" ? "Featured" : sortBy === "price-low" ? "Low→High" : "High→Low"}
                                        </span>
                                    </span>
                                    <FaChevronDown className={`text-xs text-[#3B2B9A] dark:text-indigo-300 transition-transform duration-300 ${showSortDropdown ? "rotate-180" : ""}`} />
                                </button>

                                {showSortDropdown && (
                                    <>
                                        <div
                                            className="fixed inset-0 z-10"
                                            onClick={() => setShowSortDropdown(false)}
                                        ></div>
                                        <div className="absolute right-0 mt-3 w-64 bg-white dark:bg-slate-900 rounded-2xl 
                                shadow-2xl border-2 border-slate-100 dark:border-slate-700 overflow-hidden z-20
                                animate-slide-down">
                                            {[
                                                { value: "featured", label: "Featured First", icon: "⭐" },
                                                { value: "price-low", label: "Price: Low to High", icon: "📈" },
                                                { value: "price-high", label: "Price: High to Low", icon: "📉" },
                                            ].map((option, index) => (
                                                <button
                                                    key={option.value}
                                                    onClick={() => {
                                                        setSortBy(option.value);
                                                        setShowSortDropdown(false);
                                                    }}
                                                    className={`w-full text-left px-5 py-4 
                                            hover:bg-gradient-to-r hover:from-[#3B2B9A]/5 hover:to-[#5A47D8]/5
                                            transition-all duration-200 text-sm
                                            flex items-center gap-3
                                            ${index !== 0 ? "border-t border-slate-100 dark:border-slate-700" : ""}
                                            ${sortBy === option.value
                                                            ? "bg-gradient-to-r from-[#3B2B9A]/10 to-[#5A47D8]/10 font-semibold text-[#3B2B9A] dark:text-indigo-300"
                                                            : "text-slate-700 dark:text-slate-200 font-medium"
                                                        }`}
                                                >
                                                    <span className="text-lg">{option.icon}</span>
                                                    <span className="flex-1">{option.label}</span>
                                                    {sortBy === option.value && (
                                                        <svg className="w-5 h-5 text-[#3B2B9A] dark:text-indigo-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Mobile Filters Row (Below Search on Small Screens) */}
                        <div className="flex lg:hidden gap-2 items-center overflow-x-auto pb-2 scrollbar-hide">
                            <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-900 rounded-xl border border-slate-200 dark:border-slate-600 whitespace-nowrap flex-shrink-0">
                                <FaFilter className="text-xs text-[#3B2B9A] dark:text-indigo-300" />
                                <span className="text-slate-800 dark:text-slate-100 font-semibold text-xs">
                                    Type:
                                </span>
                            </div>
                            {["All", "Plot", "Farmhouse"].map((type) => (
                                <button
                                    key={type}
                                    onClick={() => setSelectedType(type)}
                                    className={`relative px-4 py-2 rounded-xl font-semibold 
                            transition-all duration-300 whitespace-nowrap text-sm flex-shrink-0
                            ${selectedType === type
                                            ? "bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] text-white shadow-lg shadow-[#3B2B9A]/30"
                                            : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:bg-slate-950 border-2 border-slate-200 dark:border-slate-600"
                                        }`}
                                >
                                    {selectedType === type && (
                                        <span className="absolute inset-0 rounded-xl bg-white/20 animate-pulse"></span>
                                    )}
                                    <span className="relative flex items-center gap-1.5">
                                        {type === "All" && "🏘️"}
                                        {type === "Plot" && "🏗️"}
                                        {type === "Farmhouse" && "🌾"}
                                        {type}
                                    </span>
                                </button>
                            ))}
                        </div>

                        {/* Results Count with Visual Enhancement */}
                        <div className="flex items-center justify-between pt-3 border-t-2 border-slate-100 dark:border-slate-700">
                            <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600 dark:text-slate-300">
                                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] animate-pulse"></div>
                                <span>
                                    Showing <strong className="text-[#3B2B9A] dark:text-indigo-300 font-bold text-sm sm:text-base">{filteredProperties.length}</strong>{" "}
                                    {filteredProperties.length === 1 ? "property" : "properties"}
                                </span>
                            </div>

                            {(selectedType !== "All" || searchTerm) && (
                                <button
                                    onClick={() => {
                                        setSelectedType("All");
                                        setSearchTerm("");
                                    }}
                                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg 
                            bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:text-slate-100
                            transition-all duration-300 text-xs sm:text-sm font-medium
                            hover:scale-105"
                                >
                                    <FaTimes className="text-xs" />
                                    Clear Filters
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <style>{`
    @keyframes slide-down {
        0% {
            opacity: 0;
            transform: translateY(-10px);
        }
        100% {
            opacity: 1;
            transform: translateY(0);
        }
    }

    .animate-slide-down {
        animation: slide-down 0.3s ease-out;
    }

    .scrollbar-hide::-webkit-scrollbar {
        display: none;
    }
    
    .scrollbar-hide {
        -ms-overflow-style: none;
        scrollbar-width: none;
    }
`}</style>

            {/* PROPERTIES GRID - Enhanced responsive grid */}
            <section id="properties-grid" className="scroll-mt-24 py-8 sm:py-12 lg:py-16 sm:scroll-mt-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {filteredProperties.length === 0 ? (
                        <div className="text-center py-16 sm:py-20">
                            <div className="text-5xl sm:text-6xl mb-4">🏠</div>
                            <h3 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">No properties found</h3>
                            <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300">Try adjusting your search or filters</p>
                        </div>
                    ) : (
                        <div className="relative w-full">
                            <button
                                type="button"
                                aria-label="Scroll properties left"
                                disabled={!propsRowScroll.canLeft}
                                onClick={() => scrollPropertiesRow(-1)}
                                className={`absolute -left-1 top-[38%] z-30 -translate-y-1/2 sm:-left-2 lg:left-0 ${GALLERY_NAV_BTN_STYLES}`}
                            >
                                <FaChevronLeft className="text-lg sm:text-xl drop-shadow-sm" aria-hidden />
                            </button>
                            <button
                                type="button"
                                aria-label="Scroll properties right"
                                disabled={!propsRowScroll.canRight}
                                onClick={() => scrollPropertiesRow(1)}
                                className={`absolute -right-1 top-[38%] z-30 -translate-y-1/2 sm:-right-2 lg:right-0 ${GALLERY_NAV_BTN_STYLES}`}
                            >
                                <FaChevronRight className="text-lg sm:text-xl drop-shadow-sm" aria-hidden />
                            </button>

                            <div
                                ref={propertiesRowRef}
                                className="flex w-full snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pb-2 pt-1 scrollbar-hide sm:gap-6 md:grid md:grid-cols-2 md:overflow-x-visible md:snap-none lg:grid-cols-4 lg:gap-6 xl:gap-8"
                            >
                            {currentProperties.map((property) => {
                                const propertyMedia = property.media?.length
                                    ? property.media
                                    : [{ type: "image", src: property.image, label: property.title }];
                                const activeIndex = Math.min(
                                    activeMediaByProperty[property.id] ?? 0,
                                    propertyMedia.length - 1
                                );
                                const activeMedia = propertyMedia[activeIndex];

                                return <article
                                    key={property.id}
                                    className="w-[min(20rem,86vw)] shrink-0 snap-start min-w-0
                                        md:w-auto md:min-w-0
                                        bg-white dark:bg-slate-900 rounded-2xl shadow-lg overflow-hidden 
                                        transition-all duration-300 hover:shadow-2xl hover:-translate-y-2
                                        border border-slate-100 dark:border-slate-700"
                                >
                                    {/* Image */}
                                    <div className="relative h-48 sm:h-52 lg:h-56 group overflow-hidden">
                                        {activeMedia.type === "video" ? (
                                            <video
                                                key={`${property.id}-video-${activeIndex}`}
                                                className="relative z-10 w-full h-full object-contain bg-slate-100 dark:bg-slate-800"
                                                muted
                                                controls
                                                playsInline
                                                preload="auto"
                                                onPlay={handleExclusiveVideoPlay}
                                                onPause={handleExclusiveVideoPause}
                                                title="Video plays muted until you adjust volume in the controls"
                                            >
                                                <source src={activeMedia.src} type="video/mp4" />
                                                Your browser does not support the video tag.
                                            </video>
                                        ) : (
                                            <img
                                                src={activeMedia.src}
                                                alt={`${property.title} - ${activeMedia.label}`}
                                                className="w-full h-full object-contain bg-slate-100 dark:bg-slate-800 transition-transform duration-700 group-hover:scale-105"
                                            />
                                        )}
                                        {activeMedia.type !== "video" && (
                                            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                                        )}

                                        {/* Tags */}
                                        <div className="pointer-events-auto absolute top-3 sm:top-4 left-3 sm:left-4 z-20 flex flex-wrap gap-2">
                                            <div className="px-2.5 sm:px-3 py-1 rounded-full backdrop-blur-md bg-white/90 dark:bg-slate-900/80 
                                                text-[#3B2B9A] dark:text-indigo-200 font-semibold text-xs shadow-lg border border-white/40 dark:border-slate-600">
                                                {property.tag}
                                            </div>
                                            {property.featured && (
                                                <div className="px-2.5 sm:px-3 py-1 rounded-full backdrop-blur-md bg-gradient-to-r from-[#D83B29] to-[#FF4500]
                                                    text-white font-semibold text-xs shadow-lg">
                                                    ⭐ Featured
                                                </div>
                                            )}
                                        </div>

                                        {/* View Button — hidden when video is active so it does not cover play controls */}
                                        {activeMedia.type !== "video" && (
                                            <button type="button" className="pointer-events-auto absolute bottom-3 sm:bottom-4 right-3 sm:right-4 z-20 p-2.5 sm:p-3 bg-white/95 dark:bg-slate-800 backdrop-blur-sm rounded-full shadow-xl
                                                hover:bg-white dark:hover:bg-slate-700 hover:scale-110 transition-all duration-300">
                                                <FaEye size={16} className="text-[#3B2B9A] dark:text-indigo-300" />
                                            </button>
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div className="p-4 sm:p-5">
                                        <h3 className="font-bold text-base sm:text-lg text-slate-900 dark:text-slate-100 line-clamp-1">
                                            {property.title}
                                        </h3>

                                        <div className="flex items-start gap-2 mt-2 sm:mt-3 text-xs sm:text-sm text-slate-600 dark:text-slate-300">
                                            <FaMapMarkerAlt className="text-[#D83B29] mt-0.5 flex-shrink-0" />
                                            <div>
                                                <div className="font-medium">{property.location}</div>
                                                <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                                    {property.subLocation}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
                                            <div className="flex items-baseline gap-1">
                                                <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#D83B29] to-[#FF4500] bg-clip-text text-transparent">
                                                    {property.priceDisplay.split('/')[0]}
                                                </span>
                                                <span className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                                                    / sq yd
                                                </span>
                                            </div>
                                        </div>

                                        {propertyMedia.length > 1 && (
                                            <PropertyGalleryThumbnails
                                                propertyId={property.id}
                                                propertyMedia={propertyMedia}
                                                activeIndex={activeIndex}
                                                setActiveMediaByProperty={setActiveMediaByProperty}
                                            />
                                        )}

                                        <button
                                            onClick={() => setContactOpen(true)}
                                            className="mt-4 w-full py-2.5 sm:py-3 rounded-xl relative overflow-hidden
                                                bg-gradient-to-r from-[#3B2B9A] via-[#4A3AB5] to-[#5A47D8]
                                                text-white font-semibold shadow-lg
                                                transition-all duration-300 hover:shadow-xl hover:scale-[1.02]
                                                text-sm sm:text-base group"
                                        >
                                            Enquire Now
                                            <span className="absolute inset-0 bg-white/20
                                                -translate-x-full group-hover:translate-x-full
                                                transition-transform duration-700 ease-in-out
                                                skew-x-12">
                                            </span>
                                        </button>

                                    </div>
                                </article>;
                            })}
                            </div>
                        </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-center mt-8 sm:mt-12">
                            <div className="flex items-center gap-2">
                                {/* Previous Button */}
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="px-3 sm:px-4 py-2 rounded-lg border border-slate-300 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 
                                        hover:bg-slate-50 dark:bg-slate-950 disabled:opacity-50 disabled:cursor-not-allowed 
                                        transition-colors duration-200 text-sm sm:text-base font-medium"
                                >
                                    Previous
                                </button>

                                {/* Page Numbers */}
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                    <button
                                        key={page}
                                        onClick={() => setCurrentPage(page)}
                                        className={`px-3 sm:px-4 py-2 rounded-lg border transition-colors duration-200 text-sm sm:text-base font-medium ${
                                            currentPage === page
                                                ? 'bg-[#3B2B9A] border-[#3B2B9A] text-white'
                                                : 'border-slate-300 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:bg-slate-950'
                                        }`}
                                    >
                                        {page}
                                    </button>
                                ))}

                                {/* Next Button */}
                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className="px-3 sm:px-4 py-2 rounded-lg border border-slate-300 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 
                                        hover:bg-slate-50 dark:bg-slate-950 disabled:opacity-50 disabled:cursor-not-allowed 
                                        transition-colors duration-200 text-sm sm:text-base font-medium"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* CTA SECTION - Premium Card Design */}
            <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-950 dark:to-slate-900 relative overflow-hidden">
                {/* Background Decorative Elements */}
                <div className="absolute inset-0 opacity-5">
                    <div className="absolute top-10 left-10 w-72 h-72 bg-[#3B2B9A] rounded-full blur-3xl"></div>
                    <div className="absolute bottom-10 right-10 w-72 h-72 bg-[#D83B29] rounded-full blur-3xl"></div>
                </div>

                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    {/* Premium Card Container */}
                    <div className="relative group">

                        {/* Main Card */}
                        <div className="relative bg-gradient-to-br from-[#3B2B9A] via-[#4A3AB5] to-[#5A47D8] rounded-3xl shadow-2xl overflow-hidden">
                            {/* Decorative Blobs */}
                            <div className="absolute inset-0 opacity-20">
                                <div className="absolute top-0 left-0 w-64 h-64 bg-white dark:bg-slate-900 rounded-full blur-3xl"></div>
                                <div className="absolute bottom-0 right-0 w-64 h-64 bg-white dark:bg-slate-900 rounded-full blur-3xl"></div>
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-white/10 to-transparent rounded-full blur-2xl"></div>
                            </div>

                            {/* Animated Geometric Shapes */}
                            <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-10">
                                <div className="absolute top-10 right-10 w-20 h-20 border-4 border-white rounded-full animate-float-slow"></div>
                                <div className="absolute bottom-10 left-10 w-16 h-16 border-4 border-white rotate-45 animate-float"></div>
                            </div>

                            {/* Content */}
                            <div className="relative px-6 sm:px-8 lg:px-12 py-12 sm:py-14 lg:py-16 text-center">
                                {/* Icon */}
                                <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-white/20 backdrop-blur-md rounded-2xl mb-6 shadow-xl">
                                    <svg className="w-8 h-8 sm:w-10 sm:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>

                                {/* Heading */}
                                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-4 sm:mb-5 leading-tight">
                                    Didn't Find What You're Looking For?
                                </h2>

                                {/* Subheading */}
                                <p className="text-white/95 text-base sm:text-lg lg:text-xl mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed">
                                    Let us help you find your perfect property. Our expert team is ready to assist you with personalized recommendations.
                                </p>

                                {/* CTA Buttons */}
                                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                    <button
                                        onClick={() => setContactOpen(true)}
                                        className="group relative overflow-hidden px-8 py-4 bg-white dark:bg-slate-100 text-[#3B2B9A] dark:text-violet-900 font-bold rounded-full
                                shadow-2xl hover:shadow-[0_20px_60px_rgba(255,255,255,0.4)]
                                transition-all duration-300 hover:scale-105
                                text-sm sm:text-base w-full sm:w-auto"
                                    >
                                        <span className="relative z-10 flex items-center justify-center gap-2">
                                            Contact Us Today
                                            <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                            </svg>
                                        </span>
                                        <div className="absolute inset-0 bg-gradient-to-r from-[#3B2B9A]/10 to-[#5A47D8]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                    </button>

                                    <button
                                        onClick={() => setContactOpen(true)}
                                        className="px-8 py-4 bg-white/10 backdrop-blur-md text-white font-bold rounded-full
                            border-2 border-white/30 hover:border-white/60 hover:bg-white/20
                            transition-all duration-300 hover:scale-105
                            text-sm sm:text-base w-full sm:w-auto flex items-center justify-center gap-2">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                        </svg>
                                        Call Expert
                                    </button>
                                </div>

                                {/* Stats/Trust Indicators */}
                                <div className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-white/20">
                                    <div className="text-center">
                                        <div className="text-2xl sm:text-3xl font-bold text-white mb-1">24/7</div>
                                        <div className="text-xs sm:text-sm text-white/80">Support</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl sm:text-3xl font-bold text-white mb-1">1200+</div>
                                        <div className="text-xs sm:text-sm text-white/80">Happy Clients</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl sm:text-3xl font-bold text-white mb-1">100%</div>
                                        <div className="text-xs sm:text-sm text-white/80">Satisfaction</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <style>{`
    @keyframes float {
        0%, 100% { transform: translateY(0px) rotate(0deg); }
        50% { transform: translateY(-20px) rotate(5deg); }
    }

    @keyframes float-slow {
        0%, 100% { transform: translateY(0px) rotate(0deg); }
        50% { transform: translateY(-30px) rotate(-5deg); }
    }

    .animate-float { 
        animation: float 6s ease-in-out infinite; 
    }
    
    .animate-float-slow { 
        animation: float-slow 8s ease-in-out infinite; 
    }
`}</style>

            <Footer />

            {/* CONTACT MODAL */}
            {contactOpen && (
                <>
                    <div
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 opacity-100 transition-opacity duration-300"
                        onClick={() => setContactOpen(false)}
                    ></div>

                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <form
                            onSubmit={handleContactSubmit}
                            className="lr-callback-panel animate-[fadeInScale_0.35s_ease-out]"
                        >
                            <button
                                type="button"
                                aria-label="Close"
                                onClick={() => setContactOpen(false)}
                                className="
                                    absolute -top-3 -right-3
                                    bg-white dark:bg-slate-900 backdrop-blur 
                                    text-gray-700 dark:text-gray-300
                                    shadow-md hover:shadow-lg
                                    w-9 h-9 flex items-center justify-center
                                    rounded-full border border-gray-200
                                    transition-all duration-300
                                    hover:scale-110 hover:rotate-90
                                "
                            >
                                ✕
                            </button>

                            <h3 className="text-2xl font-extrabold text-[#2D1B7E] dark:text-indigo-200 tracking-tight">
                                Request a Callback
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
                                Share your details and our team will call you within one business day.
                            </p>

                            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <input
                                    name="name"
                                    autoComplete="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Full name"
                                    className="lr-callback-field"
                                />

                                <input
                                    type="email"
                                    name="email"
                                    autoComplete="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="Email address"
                                    className="lr-callback-field"
                                />

                                <input
                                    type="tel"
                                    name="phone"
                                    inputMode="numeric"
                                    autoComplete="tel"
                                    maxLength={10}
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    placeholder="Mobile (10 digits, starts 6–9)"
                                    className="lr-callback-field"
                                />

                                <div className="relative" onClick={(e) => e.stopPropagation()}>
                                    <button
                                        type="button"
                                        onClick={() => setInterestOpen(!interestOpen)}
                                        className="lr-callback-field flex items-center justify-between"
                                    >
                                        <span className={interest === "I'm interested in" ? "text-slate-400" : "text-slate-700 dark:text-slate-200"}>
                                            {interest}
                                        </span>

                                        <svg className={`w-4 h-4 transition-transform ${interestOpen ? "rotate-180" : ""}`}
                                            fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>

                                    {interestOpen && (
                                        <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-600 rounded-lg shadow-xl z-50 overflow-hidden animate-fadeIn">
                                            {["Buying", "Selling", "Investment"].map((item) => (
                                                <div
                                                    key={item}
                                                    className="px-4 py-2 hover:bg-[#D83B29]/10 cursor-pointer text-slate-700 dark:text-slate-200"
                                                    onClick={() => {
                                                        setInterest(item);
                                                        setInterestOpen(false);
                                                    }}
                                                >
                                                    {item}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <textarea
                                name="message"
                                rows="4"
                                value={formData.message}
                                onChange={handleInputChange}
                                placeholder="Message (optional)"
                                className="lr-callback-field resize-none"
                            ></textarea>

                            <div className="mt-6 flex items-center justify-between">
                                <button
                                    type="submit"
                                    disabled={!callbackFormValid || enquirySubmitting}
                                    className="
        relative overflow-hidden group
        px-6 py-3 rounded-xl text-white font-semibold
        bg-gradient-to-r from-[#8B1E1E] to-[#D83B29]
        shadow-lg hover:shadow-xl
        hover:brightness-105 transition-all duration-300
        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:brightness-100
    "
                                >
                                    {enquirySubmitting ? "Sending..." : "Submit Request"}

                                    <span
                                        className="
            absolute inset-0 bg-white/20
            translate-x-[-120%]
            group-hover:translate-x-[120%]
            transition-all duration-700 ease-in-out
            skew-x-[45deg]
        "
                                    ></span>
                                </button>

                                <small className="text-gray-600 dark:text-gray-400">We respect your privacy.</small>
                            </div>
                        </form>
                    </div>

                    <style>
                        {`
                @keyframes fadeInScale {
                    0% { opacity: 0; transform: translateY(20px) scale(0.95); }
                    100% { opacity: 1; transform: translateY(0) scale(1); }
                }
                            .scrollbar-hide::-webkit-scrollbar {
                                display: none;
                            }
                            .scrollbar-hide {
                                -ms-overflow-style: none;
                                scrollbar-width: none;
                            }
            `}
                    </style>
                </>
            )}
        </div>
    );
}